import { sum, sumBy, uniq } from "lodash-es";
import { storeToRefs } from "pinia";
import { type CSSProperties } from "vue";

import {
  getLocalePluralText,
  getLocaleText,
  getLocaleValue,
} from "shared/boot/i18n";
import { sentimentOptions } from "shared/components/core/pickers/SentimentPicker";
import {
  type DateValue,
  formatIntlDate,
  unixDateTime,
} from "shared/helpers/date";
import DateRange from "shared/helpers/DateRange";
import {
  filterSocialPlatformsByLabels,
  getSourceFromGroup,
  getTypesForPlatform,
  MediumField,
  type MediumName,
  SocialPlatform,
} from "shared/helpers/media";
import { prettyNumber, suffix } from "shared/helpers/number";
import { isSocialStream, streamDisplayLabel } from "shared/helpers/stream";
import { type InitialStreamFilters } from "shared/helpers/StreamFilters";
import getAppConfig from "shared/helpers/useAppConfig";
import {
  ChartsWithFixedPlatformMap,
  DEFAULT_MAX_SELECTED_INSTAGRAM_ACCOUNTS,
} from "shared/modules/Charts/constants";
import {
  type ChartSetting,
  ChartVariant,
  type ChartWithFixedPlatform,
} from "shared/modules/Charts/types";
import {
  type ChartConfigIndex,
  SidebarComponent,
} from "shared/modules/Charts/types/chartConfig";
import {
  type DataPoint,
  type DataPointClickEvent,
  type DatasetClickEvent,
  type EmojiCountEvent,
  type GeographicSpreadClickEvent,
  type MentionBenchmarkTimelineClickEvent,
  type TopFacebookPagesEvent,
  type TopInfluencersEvent,
  type WordCloudClickEvent,
} from "shared/modules/Charts/types/chartInteractions";
import {
  type ChartLibraryData,
  type ChartLibraryProps,
  ChartLibraryTagId,
  type GeographicSpreadData,
  type KeyMessagesData,
  type LeadingSourcesData,
  type ReactScoreBreakdownData,
  type SentimentBreakdownData,
  type ShareOfVoiceData,
  type ShareOfVoiceTimelineData,
} from "shared/modules/Charts/types/data";
import {
  isChartSettingWithFixedPlatform,
  isChartSettingWithMediaReleases,
  isChartSettingWithStreamsAndMediaTypes,
  isDataPointClickEvent,
  isDatasetClickEvent,
} from "shared/modules/Charts/types/guards";
import { useOrganisationUsageStore } from "shared/modules/OrganisationUsage/stores/useOrganisationUsageStore";
import useStreamsStore from "shared/stores/streams";
import { type Maybe, type Nullable, type Stream } from "shared/types";
import {
  type MentionType,
  SHARE_TYPES_BY_PLATFORM,
  type ShareTypePlatform,
} from "shared/types/mentions";

interface ClickedRange {
  after: number;
  before: number;
}

const MAX_ONLINE_PAGE_RANK = 5;
const MAX_PRINT_PAGE = 5;

const DEFAULT_STYLE: CSSProperties = {
  height: "300px",
  overflow: "clip",
  padding: "15px",
} as const;

const TIMELINE_DEFAULT: CSSProperties = {
  ...DEFAULT_STYLE,
  height: "390px",
} as const;

const WORD_CLOUD_DEFAULT: CSSProperties = {
  ...DEFAULT_STYLE,
  height: "390px",
} as const;

const BAR_DEFAULT: CSSProperties = {
  ...DEFAULT_STYLE,
  height: "330px",
} as const;

const PIE_DEFAULT: CSSProperties = {
  ...DEFAULT_STYLE,
  height: "228px",
} as const;

const assetsUrl = (path: string): string =>
  `${import.meta.env.VITE_ASSETS_URL}${path}`;

function isOfTypeMediumField(
  keyInput: string | undefined
): keyInput is MediumField {
  return Object.values(MediumField).includes(keyInput as MediumField);
}

function clickedRange(
  dateKey: DateValue,
  chartProps: ChartLibraryProps,
  chartRange: DateRange
): ClickedRange {
  const { data, options } = chartProps;

  const variant = options?.chart_variant;

  const labels = [
    ...(((data as { labels?: DateValue[] })?.labels ?? []) as DateValue[]),
  ];

  if (variant === ChartVariant.StackedHorizontalBar && labels.length > 0) {
    labels.reverse();
  }

  const clickedLabelTime = unixDateTime(dateKey);

  const clickedLabelIndex = labels.findIndex(
    (label) => unixDateTime(label) === clickedLabelTime
  );

  const nextLabel =
    clickedLabelIndex === -1 ? undefined : labels[clickedLabelIndex + 1];

  const bucketAfter = clickedLabelTime;
  const bucketBefore = nextLabel ? unixDateTime(nextLabel) : chartRange.before;

  const bucketOverlapsChartRange =
    bucketAfter < chartRange.before && bucketBefore > chartRange.after;

  if (!bucketOverlapsChartRange) {
    return { after: bucketAfter, before: bucketBefore };
  }

  const after = Math.max(bucketAfter, chartRange.after);
  const before = Math.min(bucketBefore, chartRange.before);

  return { after, before };
}

function formatChartInteractionDate(
  dateKey: DateValue,
  chartProps: ChartLibraryProps
): string {
  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
  };

  if (chartProps.options?.time_zone) {
    options.timeZone = chartProps.options.time_zone;
  }

  return formatIntlDate(dateKey, options);
}

function frontPageBucketRange(
  datasetIndex: number,
  maxValue: number
): { max: number; min: number } {
  // Dataset 0 is the grouped 2-5 bucket; the other dataset is the single 1 bucket.
  return datasetIndex === 0 ? { max: maxValue, min: 2 } : { max: 1, min: 1 };
}

export function findStreamById(
  id: number,
  streamsData?: Stream[]
): Maybe<Stream> {
  const { getStreamById } = useStreamsStore();

  let selectedStream = getStreamById(id);

  if (!selectedStream && streamsData) {
    selectedStream = streamsData.find((stream) => stream.id === id);
  }

  return selectedStream;
}

function normalizeFixedPlatformChartSetting<T extends ChartSetting>(
  chartSetting: T
): T {
  if (!isChartSettingWithStreamsAndMediaTypes(chartSetting)) {
    return chartSetting;
  }

  if (!isChartSettingWithFixedPlatform(chartSetting)) {
    return chartSetting;
  }

  const fixedPlatform =
    ChartsWithFixedPlatformMap[chartSetting.chart as ChartWithFixedPlatform];

  if (!fixedPlatform) {
    return chartSetting;
  }

  return {
    ...chartSetting,
    types: getTypesForPlatform(fixedPlatform),
  };
}

function normalizeMediaReleaseChartSetting<T extends ChartSetting>(
  chartSetting: T
): T {
  if (!isChartSettingWithMediaReleases(chartSetting)) {
    return chartSetting;
  }

  const persistedChartSetting = chartSetting as T & {
    media_release_ids?: number[];
    media_release_ids_by_stream?: Record<string, number[]>;
  };

  const {
    media_release_ids_by_stream: mediaReleaseIdsByStream,
    ...chartSettingWithoutLegacyMediaReleases
  } = persistedChartSetting;

  return {
    ...chartSettingWithoutLegacyMediaReleases,
    media_release_ids:
      persistedChartSetting.media_release_ids ??
      uniq(Object.values(mediaReleaseIdsByStream ?? {}).flat()),
  } as T;
}

export function normalizeChartSetting<T extends ChartSetting>(
  chartSetting: T
): T {
  return normalizeMediaReleaseChartSetting(
    normalizeFixedPlatformChartSetting(chartSetting)
  );
}

export function mediaItemsString(
  value: number,
  type?: Nullable<MediumName>
): string {
  if (type) {
    return getLocalePluralText(
      "instant_insights_charts.media_items_with_type",
      value,
      {
        type: getLocaleText(
          `helpers.sources.${String(type).toLowerCase()}.label`
        ),
        value: prettyNumber(value),
      }
    );
  }

  return getLocalePluralText("instant_insights_charts.media_items", value, {
    value: prettyNumber(value),
  });
}

export function socialItemsString(value: number): string {
  return getLocaleText("social_analyse_charts.social_items", {
    count: value,
    valueWithSuffix: suffix(value),
  });
}

export function getFormattedMedia(media: MediumName): MediumName {
  return media === "Influencer Social" ? "Social" : media;
}

export function getChartConfigs({
  streamsData,
}: {
  streamsData?: Stream[];
}): ChartConfigIndex {
  const appConfig = getAppConfig();

  const { aiVisibilityUsage } = storeToRefs(useOrganisationUsageStore());

  const isAIVisibilityButtonDisabled = () =>
    aiVisibilityUsage.value.hasReachedLimit;

  const aiVisibilityButtonTooltip = () =>
    aiVisibilityUsage.value.hasReachedLimit
      ? getLocaleText(
          "insights_dashboard_templates.ai_visibility.disabled_tooltip"
        )
      : "";

  return {
    advertising_value_equivalency: {
      component: "AdvertisingValueEquivalency",
      featureFlag: "advertising_value_widget",
      interactionEnabled: false,
      label: getLocaleValue(
        "instant_insights_charts.advertising_value_equivalency.title"
      ),
      method: "advertising_value_equivalency",
      options: {},
      style: { ...TIMELINE_DEFAULT },
    },
    automated_sentiment: {
      component: "SentimentBreakdown",
      componentAttributes: {
        legendPosition: "right",
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartData) {
        const eventData = clickedProps as DatasetClickEvent;

        const label = Object.keys(eventData)[0];
        const { dataIndex, value } = eventData[label];
        const { fields } = chartData.data as SentimentBreakdownData;
        const sentimentKey = fields![dataIndex];

        const title = label;
        const subTitle = mediaItemsString(Number(value));

        const sentimentOption = sentimentOptions.find(
          (option) => option.field === sentimentKey
        );

        const [min, max] = sentimentOption!.range;

        return {
          filterProps: {
            sentiment: [{ max, min }],
            sentimentEnabled: true,
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.automated_sentiment.title"
      ),
      method: "automated_sentiment",
      options: {},
      style: { ...PIE_DEFAULT },
    },
    average_post_time_by_platform: {
      chart: "average_post_time_by_platform",
      component: "BroadcastTimeByMediaType",
      interactionEnabled: false,
      label: getLocaleValue(
        "instant_insights_charts.average_post_time_by_platform.title"
      ),
      method: "average_post_time_by_platform",
      options: {},
      style: { ...DEFAULT_STYLE },
    },
    coverage_by_media_type: {
      component: "CoverageByMediaType",
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as
          | DataPointClickEvent
          | DatasetClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const media = Object.keys(dataObj)[0] as keyof DatasetClickEvent;
        const { dataIndex, datasetIndex, value } = dataObj[media];
        let index = dataIndex;

        if (
          [
            ChartVariant.Line,
            ChartVariant.Area,
            ChartVariant.StackedHorizontalBar,
            ChartVariant.StackedVerticalBar,
          ].includes(chartProps.options?.chart_variant ?? ("" as ChartVariant))
        ) {
          index = datasetIndex;
        }

        const { platform } = chartProps.legend![index];
        const source = getSourceFromGroup(platform!);

        const title = getLocaleText(
          "instant_insights_charts.coverage_by_media_type.popup_title",
          { media }
        );

        const subTitle = mediaItemsString(Number(value), source.medium);

        return {
          filterProps: {
            media: [source.medium],
            range: chartRange,
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.coverage_by_media_type.title"
      ),
      method: "coverage_by_media_type",
      options: {},
      style: { ...BAR_DEFAULT },
    },
    coverage_volume_and_analytics_timeline: {
      component: "CoverageVolumeAndPotentialAudienceReach",
      componentAttributes: {
        legendLayout: "inline",
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.coverage_volume_and_analytics_timeline.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.coverage_volume_and_analytics_timeline.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_vertical_bar_chart.svg"),
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as
          | DataPointClickEvent
          | DatasetClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const dateKey = Object.keys(eventData)[0];

        const breakdownKey = Object.keys(dataObj)[0] as MediumName;

        const { stream } = chartProps;

        const selectedStream = stream
          ? findStreamById(stream.id, streamsData)
          : null;

        const isSocial = isSocialStream(selectedStream);

        const formattedBreakdownKey: MediumName = isSocial
          ? "Social"
          : getFormattedMedia(breakdownKey);

        const { dataIndex } = dataObj[breakdownKey!];
        let { value } = dataObj[breakdownKey!];

        const { after, before } = clickedRange(
          dateKey,
          chartProps,
          chartRange!
        );

        const title = formatChartInteractionDate(dateKey, chartProps);

        if (!isOfTypeMediumField(formattedBreakdownKey?.toLowerCase())) {
          value = sum(
            (chartProps.data as ChartLibraryData)
              .datasets!.filter(
                (dataset) =>
                  (dataset as unknown as { yAxisID: string }).yAxisID ===
                  "volume"
              )
              .map((dataset) => dataset.data[dataIndex])
          );
        }

        const subTitle = isSocial
          ? socialItemsString(Number(value))
          : mediaItemsString(Number(value), formattedBreakdownKey);

        const filterProps: Partial<InitialStreamFilters<string>> = {
          dateRangeEnabled: true,
          media: [formattedBreakdownKey],
          range: new DateRange({ after, before }),
        };

        if (isSocial) {
          const platformLabels = Object.keys(eventData[dateKey]);

          filterProps.platforms = filterSocialPlatformsByLabels(
            platformLabels
          ).map(({ platform }) => platform);
        }

        return {
          filterProps,
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.coverage_volume_and_analytics_timeline.title"
      ),
      method: "coverage_volume_and_analytics",
      style: { ...TIMELINE_DEFAULT, height: "355px" },
    },
    coverage_volume_and_potential_audience_reach_table: {
      component: "CoverageVolumeAndPotentialAudienceReach",
      componentAttributes: {
        legendPosition: "right",
        truncateLabels: true,
      },
      featureFlag: "dashboard_potential_reach",
      interactionEnabled: false,
      label: getLocaleValue(
        "instant_insights_charts.coverage_volume_and_potential_audience_reach_table.title"
      ),
      method: "new_coverage_volume_and_potential_audience_reach",
      options: {
        visualisation_type: "table",
      },
      style: {
        ...DEFAULT_STYLE,
        height: "320px",
        padding: "8px",
      },
    },
    coverage_volume_and_potential_audience_reach_timeline: {
      component: "CoverageVolumeAndPotentialAudienceReach",
      componentAttributes: {
        legendLayout: "inline",
      },
      featureFlag: "dashboard_potential_reach",
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as
          | DatasetClickEvent
          | DataPointClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const dateKey = Object.keys(eventData)[0];

        const breakdownKey = Object.keys(dataObj)[0] as MediumName;

        let formattedBreakdownKey: Nullable<MediumName> =
          getFormattedMedia(breakdownKey);

        const { dataIndex } = dataObj[breakdownKey!];
        let { value } = dataObj[breakdownKey!];

        const { after, before } = clickedRange(
          dateKey,
          chartProps,
          chartRange!
        );

        const title = formatChartInteractionDate(dateKey, chartProps);

        if (!isOfTypeMediumField(formattedBreakdownKey?.toLowerCase())) {
          formattedBreakdownKey = null;

          value = sum(
            (chartProps.data as ChartLibraryData)
              .datasets!.filter(
                (dataset) =>
                  (dataset as unknown as { yAxisID: string }).yAxisID ===
                  "volume"
              )
              .map((dataset) => dataset.data[dataIndex])
          );
        }

        const subTitle = mediaItemsString(Number(value), formattedBreakdownKey);

        return {
          filterProps: {
            dateRangeEnabled: true,
            media: formattedBreakdownKey ? [formattedBreakdownKey] : [],
            range: new DateRange({ after, before }),
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.coverage_volume_and_potential_audience_reach_timeline.title"
      ),
      method: "new_coverage_volume_and_potential_audience_reach",
      options: {
        visualisation_type: "chart",
      },
      style: { ...TIMELINE_DEFAULT, height: "355px" },
    },
    coverage_volume_and_referral_traffic_timeline: {
      component: "CoverageVolumeAndPotentialAudienceReach",
      componentAttributes: {
        legendLayout: "inline",
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.coverage_volume_and_referral_traffic_timeline.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.coverage_volume_and_referral_traffic_timeline.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_vertical_bar_chart.svg"),
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as DataPointClickEvent;

        const dateKey = Object.keys(eventData)[0];

        const { after, before } = clickedRange(
          dateKey,
          chartProps,
          chartRange!
        );

        const title = formatChartInteractionDate(dateKey, chartProps);

        let total = 0;

        Object.keys(eventData[dateKey]).forEach((key) => {
          if (key === "Online") {
            total += Number(eventData[dateKey][key].value);
          }
        });

        const subTitle = mediaItemsString(total);

        const data = chartProps.data as ChartLibraryData;
        const referredUrlsCache = data.referred_urls_cache;

        return {
          filterProps: {
            dateRangeEnabled: true,
            media: ["Online"],
            range: new DateRange({ after, before }),
            referredUrlsCache,
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.coverage_volume_and_referral_traffic_timeline.title"
      ),
      method: "coverage_volume_and_referral_traffic",
      style: { ...TIMELINE_DEFAULT, height: "355px" },
    },
    coverage_volume_and_stock_price: {
      component: "CoverageVolumeAndStockPrice",
      componentAttributes: {
        legendLayout: "inline",
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.coverage_volume_and_stock_price.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.coverage_volume_and_stock_price.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_line_chart.svg"),
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as
          | DatasetClickEvent
          | DataPointClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const dateKey = Object.keys(eventData)[0];

        const breakdownKey = Object.keys(dataObj)[0] as Nullable<MentionType>;

        const { datasetIndex, value } = dataObj[breakdownKey!];

        const { after, before } = clickedRange(
          dateKey,
          chartProps,
          chartRange!
        );

        const title = formatChartInteractionDate(dateKey, chartProps);

        const dataset = (chartProps.data as ChartLibraryData).datasets![
          datasetIndex
        ];

        const streamId = dataset.stream_id;
        const isStockPriceDataset = dataset.yAxisID === "stock_price";
        const isLLMDataset = dataset.tag_id === ChartLibraryTagId.LLM;

        const selectedStream = streamId
          ? findStreamById(streamId, streamsData)
          : undefined;

        const isSocial = isSocialStream(selectedStream);

        const total = Number(value || 0);

        const subTitle = isSocial
          ? socialItemsString(total)
          : mediaItemsString(total);

        return {
          filterProps: {
            dateRangeEnabled: true,
            range: new DateRange({ after, before }),
            ...(isSocial ? { media: ["Social"] } : {}),
          },
          preventClick:
            isStockPriceDataset || !value || (!streamId && !isLLMDataset),
          selectedStream,
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.coverage_volume_and_stock_price.title"
      ),
      method: "coverage_volume_and_stock_price",
      options: {
        clip: {
          bottom: 15,
          left: 15,
          right: 15,
          top: 15,
        },
      },
      requiresStockSymbol: true,
      settingsComponent: SidebarComponent.ChartSettingsStockSymbolForm,
      style: { ...TIMELINE_DEFAULT, height: "355px" },
    },
    coverage_volume_breakdown: {
      component: "CoverageByMediaType",
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as
          | DataPointClickEvent
          | DatasetClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const media = Object.keys(dataObj)[0] as keyof DatasetClickEvent;
        const { dataIndex, datasetIndex, value } = dataObj[media];

        let index = dataIndex;

        if (
          [
            ChartVariant.Line,
            ChartVariant.Area,
            ChartVariant.StackedHorizontalBar,
            ChartVariant.StackedVerticalBar,
          ].includes(chartProps.options?.chart_variant ?? ("" as ChartVariant))
        ) {
          index = datasetIndex;
        }

        const { platform } = chartProps.legend![index];

        const title = getLocaleText(
          "instant_insights_charts.coverage_volume_breakdown.popup_title",
          { media }
        );

        const subTitle = socialItemsString(Number(value));

        return {
          filterProps: {
            media: ["Social"],
            platforms: [platform as SocialPlatform],
            range: chartRange,
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.coverage_volume_breakdown.title"
      ),
      method: "coverage_by_media_type",
      options: {},
      style: { ...BAR_DEFAULT },
    },
    coverage_volume_timeline: {
      component: "CoverageVolumeTimeline",
      componentAttributes: {
        legendLayout: "inline",
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as DatasetClickEvent;

        const dateKey = Object.keys(eventData)[0] as keyof DatasetClickEvent;

        const media = Object.keys(eventData[dateKey])[0] as MediumName;

        const { stream } = chartProps;

        const selectedStream = stream
          ? findStreamById(stream.id, streamsData)
          : null;

        const isSocial = isSocialStream(selectedStream);

        const formattedMedia: MediumName = isSocial
          ? "Social"
          : getFormattedMedia(media);

        const { after, before } = clickedRange(
          dateKey,
          chartProps,
          chartRange!
        );

        const title = formatChartInteractionDate(dateKey, chartProps);

        const total = sumBy(Object.values(eventData[dateKey]), "value");

        const subTitle = isSocial
          ? socialItemsString(total)
          : mediaItemsString(total, formattedMedia);

        const filterProps: Partial<InitialStreamFilters<string>> = {
          dateRangeEnabled: true,
          media: [formattedMedia],
          range: new DateRange({ after, before }),
        };

        if (isSocial) {
          const platformLabels = Object.keys(eventData[dateKey]);

          filterProps.platforms = filterSocialPlatformsByLabels(
            platformLabels
          ).map(({ platform }) => platform);
        }

        return {
          filterProps,
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.coverage_volume_timeline.title"
      ),
      method: "coverage_volume_timeline",
      options: {},
      style: { ...TIMELINE_DEFAULT },
    },
    desktop_vs_mobile_readership: {
      component: "DesktopVsMobileReadership",
      componentAttributes: {
        legendPosition: "left",
      },
      label: getLocaleValue(
        "instant_insights_charts.desktop_vs_mobile_readership.title"
      ),
      method: "desktop_vs_mobile_readership",
      options: {
        cutout: 40,
      },
      style: {
        ...PIE_DEFAULT,
        height: "280px",
      },
    },
    domain_authority: {
      component: "DomainAuthority",
      componentAttributes: {
        legendPosition: "right",
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as DatasetClickEvent;

        const domain = Object.keys(eventData)[0];
        const { dataIndex, value } = eventData[domain];

        const title = domain;
        const subTitle = mediaItemsString(Number(value));

        const { ranges } = chartProps;

        const { from, to } = ranges![dataIndex];

        return {
          filterProps: {
            domainAuthority: {
              max: to,
              min: from,
            },
            range: chartRange,
          },
          setFilters: true,
          subTitle,
          title,
        };
      },
      label: getLocaleValue("instant_insights_charts.domain_authority.title"),
      method: "domain_authority",
      options: {},
      style: { ...BAR_DEFAULT },
    },
    emoji_count: {
      chart: "emoji_count",
      component: "EmojiCount",
      componentAttributes: {
        selectionEnabled: true,
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as EmojiCountEvent;
        const { emoji, total } = eventData;

        const title = getLocaleText("social_analyse_charts.emoji_mentions", {
          emoji: emoji.value,
        });

        const subTitle = socialItemsString(Number(total.value));

        return {
          filterProps: {
            keywords: emoji.value,
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue("instant_insights_charts.emoji_count.title"),
      method: "emoji_count",
      options: {
        size: 70,
      },
      style: { height: "296px" },
    },
    engagement_timeline_for_social_post: {
      component: "CoverageVolumeTimeline",
      componentAttributes: {
        legendLayout: "inline",
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.engagement_timeline_for_social_post.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.engagement_timeline_for_social_post.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_line_chart.svg"),
      },
      label: getLocaleValue(
        "instant_insights_charts.engagement_timeline_for_social_post.title"
      ),
      maxSelectedPosts: 1,
      method: "engagement_timeline_for_social_post",
      requiresSocialPostSelection: true,
      selectsFirstPostByDefault: true,
      style: { ...TIMELINE_DEFAULT },
    },
    facebook_engagement_metric: {
      component: "FacebookEngagementMetric",
      label: getLocaleValue(
        "instant_insights_charts.facebook_engagement_metric.title"
      ),
      method: "facebook_engagement_metric",
    },
    follower_growth: {
      component: "CoverageVolumeTimeline",
      componentAttributes: {
        legendLayout: "inline",
      },
      label: getLocaleValue("instant_insights_charts.follower_growth.title"),
      method: "follower_growth",
      style: { ...TIMELINE_DEFAULT },
    },
    follower_growth_benchmark: {
      component: "DataTableWithChange",
      componentAttributes: {
        legendLayout: "inline",
      },
      label: getLocaleValue(
        "instant_insights_charts.follower_growth_benchmark.title"
      ),
      maxStreams: 30,
      method: "follower_growth_benchmark",
      options: {
        include_count_in_legend: true,
      },
      style: { ...TIMELINE_DEFAULT },
    },
    follower_growth_benchmark_by_account: {
      component: "DataTableWithChange",
      componentAttributes: {
        legendLayout: "inline",
      },
      label: getLocaleValue(
        "instant_insights_charts.follower_growth_benchmark_by_account.title"
      ),
      maxStreams: 30,
      method: "follower_growth_benchmark_by_account",
      options: {
        include_count_in_legend: true,
      },
      style: { ...TIMELINE_DEFAULT },
    },
    front_page_online: {
      component: "VolumeOfCoverageByPosition",
      featureFlag: "read_online_article",
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as DataPointClickEvent;
        const dateKey = Object.keys(eventData)[0];
        const point = Object.values(eventData[dateKey])[0];

        const { after, before } = clickedRange(
          dateKey,
          chartProps,
          chartRange!
        );

        const title = formatChartInteractionDate(dateKey, chartProps);

        const subTitle = mediaItemsString(Number(point.value));

        const frontPageRanks = frontPageBucketRange(
          point.datasetIndex,
          MAX_ONLINE_PAGE_RANK
        );

        return {
          filterProps: {
            dateRangeEnabled: true,
            frontPageRanks,
            frontPageRanksEnabled: true,
            media: ["Online"],
            range: new DateRange({ after, before }),
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue("instant_insights_charts.front_page_online.title"),
      method: "volume_of_coverage_by_position",
      options: {
        medium: "Online",
      },
      style: { ...TIMELINE_DEFAULT },
    },
    front_page_print: {
      component: "VolumeOfCoverageByPosition",
      featureFlag: "view_paper_articles",
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as DataPointClickEvent;
        const dateKey = Object.keys(eventData)[0];
        const point = Object.values(eventData[dateKey])[0];

        const { after, before } = clickedRange(
          dateKey,
          chartProps,
          chartRange!
        );

        const title = formatChartInteractionDate(dateKey, chartProps);

        const subTitle = mediaItemsString(Number(point.value));

        const pageNumbers = frontPageBucketRange(
          point.datasetIndex,
          MAX_PRINT_PAGE
        );

        return {
          filterProps: {
            dateRangeEnabled: true,
            media: ["Print"],
            pageNumbers,
            pageNumbersEnabled: true,
            range: new DateRange({ after, before }),
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue("instant_insights_charts.front_page_print.title"),
      method: "volume_of_coverage_by_position",
      options: {
        medium: "Print",
      },
      style: { ...TIMELINE_DEFAULT },
    },
    geographic_spread_city_chart: {
      component: "GeographicSpread",
      componentAttributes: {
        legendLayout: "inline",
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartData) {
        const eventData = clickedProps as
          | DatasetClickEvent
          | DataPointClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const locationName = Object.keys(dataObj)[0];

        const locationData = (chartData.data as GeographicSpreadData).map!
          .datasets[dataObj[locationName].dataIndex];

        const title = locationName;

        const subTitle = mediaItemsString(Number(dataObj[locationName].value));

        return {
          filterProps: {
            locations: [locationData.location_id],
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.geographic_spread_city_chart.title"
      ),
      method: "new_geographic_spread",
      options: {
        anz_only: appConfig.value.geoChartANZOnly,
        geographic_spread_scope: "city",
        visualisation_type: "chart",
      },
      style: { ...TIMELINE_DEFAULT, overflow: "auto" },
    },
    geographic_spread_city_map: {
      component: "GeographicSpread",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as GeographicSpreadClickEvent;

        const { location: locationName, total } = eventData;

        const title = locationName;
        const subTitle = mediaItemsString(total);

        return {
          filterProps: {
            locations: [eventData.location_id],
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.geographic_spread_city_map.title"
      ),
      method: "new_geographic_spread",
      options: {
        anz_only: appConfig.value.geoChartANZOnly,
        geographic_spread_scope: "city",
        visualisation_type: "map",
      },
      style: { ...DEFAULT_STYLE, padding: 0 },
    },
    geographic_spread_country_chart: {
      component: "GeographicSpread",
      componentAttributes: {
        legendLayout: "inline",
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartData) {
        const eventData = clickedProps as
          | DatasetClickEvent
          | DataPointClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const locationName = Object.keys(dataObj)[0];

        const locationData = (chartData.data as GeographicSpreadData).map!
          .datasets[dataObj[locationName].dataIndex];

        const title = locationName;

        const subTitle = mediaItemsString(Number(dataObj[locationName].value));

        return {
          filterProps: {
            locations: [locationData.location_id],
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.geographic_spread_country_chart.title"
      ),
      method: "new_geographic_spread",
      options: {
        anz_only: appConfig.value.geoChartANZOnly,
        geographic_spread_scope: "country",
        visualisation_type: "chart",
      },
      style: { ...TIMELINE_DEFAULT, overflow: "auto" },
    },
    geographic_spread_country_map: {
      component: "GeographicSpread",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as GeographicSpreadClickEvent;

        const { location: locationName, total } = eventData;

        const title = locationName;
        const subTitle = mediaItemsString(total);

        return {
          filterProps: {
            locations: [eventData.location_id],
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.geographic_spread_country_map.title"
      ),
      method: "new_geographic_spread",
      options: {
        anz_only: appConfig.value.geoChartANZOnly,
        geographic_spread_scope: "country",
        visualisation_type: "map",
      },
      style: { ...DEFAULT_STYLE, padding: 0 },
    },
    geographic_spread_region_chart: {
      component: "GeographicSpread",
      componentAttributes: {
        legendLayout: "inline",
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartData) {
        const eventData = clickedProps as
          | DatasetClickEvent
          | DataPointClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const locationName = Object.keys(dataObj)[0];

        const locationData = (chartData.data as GeographicSpreadData).map!
          .datasets[dataObj[locationName].dataIndex];

        const title = locationName;

        const subTitle = mediaItemsString(Number(dataObj[locationName].value));

        return {
          filterProps: {
            locations: [locationData.location_id],
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.geographic_spread_region_chart.title"
      ),
      method: "new_geographic_spread",
      options: {
        geographic_spread_scope: "region",
        visualisation_type: "chart",
      },
      style: { ...TIMELINE_DEFAULT, overflow: "auto" },
    },
    geographic_spread_region_map: {
      component: "GeographicSpread",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as GeographicSpreadClickEvent;

        const { location: locationName, total } = eventData;

        const title = locationName;
        const subTitle = mediaItemsString(total);

        return {
          filterProps: {
            locations: [eventData.location_id],
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.geographic_spread_region_map.title"
      ),
      method: "new_geographic_spread",
      options: {
        geographic_spread_scope: "region",
        visualisation_type: "map",
      },
      style: { ...DEFAULT_STYLE, padding: 0 },
    },
    geographic_spread_state_chart: {
      component: "GeographicSpread",
      componentAttributes: {
        legendLayout: "inline",
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartData) {
        const eventData = clickedProps as
          | DatasetClickEvent
          | DataPointClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const locationName = Object.keys(dataObj)[0];

        const locationData = (chartData.data as GeographicSpreadData).map!
          .datasets[dataObj[locationName].dataIndex];

        const title = locationName;

        const subTitle = mediaItemsString(Number(dataObj[locationName].value));

        return {
          filterProps: {
            locations: [locationData.location_id],
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.geographic_spread_state_chart.title"
      ),
      method: "new_geographic_spread",
      options: {
        anz_only: appConfig.value.geoChartANZOnly,
        geographic_spread_scope: "state",
        visualisation_type: "chart",
      },
      style: { ...TIMELINE_DEFAULT, overflow: "auto" },
    },
    geographic_spread_state_map: {
      component: "GeographicSpread",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as GeographicSpreadClickEvent;

        const { location: locationName, total } = eventData;

        const title = locationName;
        const subTitle = mediaItemsString(total);

        return {
          filterProps: {
            locations: [eventData.location_id],
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.geographic_spread_state_map.title"
      ),
      method: "new_geographic_spread",
      options: {
        anz_only: appConfig.value.geoChartANZOnly,
        geographic_spread_scope: "state",
        visualisation_type: "map",
      },
      style: { ...DEFAULT_STYLE, padding: 0 },
    },
    instagram_profile_activity: {
      component: "ShareOfVoice",
      componentAttributes: {
        legendPosition: "right",
        truncateLabels: true,
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.social_post_highlight.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.social_post_highlight.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_table.svg"),
        style: {
          width: "auto",
        },
      },
      label: getLocaleValue("social_analyse_charts.instagram_profile_activity"),
      maxStreams: 30,
      method: "instagram_profile_activity",
      options: {
        visualisation_type: "chart",
      },
      requiresSocialPostSelection: true,
      style: {
        ...PIE_DEFAULT,
        height: "320px",
        overflow: "visible",
      },
    },
    instagram_public_account_follower_growth: {
      component: "CoverageVolumeTimeline",
      componentAttributes: {
        legendLayout: "inline",
      },
      label: getLocaleValue(
        "instant_insights_charts.instagram_public_account_follower_growth.title"
      ),
      maxSelectedAccounts: DEFAULT_MAX_SELECTED_INSTAGRAM_ACCOUNTS,
      method: "instagram_public_account_follower_growth",
      socialAccountSelector: "instagram_handle",
      style: { ...TIMELINE_DEFAULT },
    },
    instagram_public_and_owned_account_followers_benchmark: {
      component: "DataTableWithChange",
      componentAttributes: {
        legendLayout: "inline",
      },
      label: getLocaleValue(
        "instant_insights_charts.instagram_public_and_owned_account_followers_benchmark.title"
      ),
      maxSelectedAccounts: DEFAULT_MAX_SELECTED_INSTAGRAM_ACCOUNTS,
      method: "instagram_public_and_owned_account_followers_benchmark",
      options: {
        include_count_in_legend: true,
      },
      socialAccountSelector: "instagram_handle",
      style: { ...TIMELINE_DEFAULT },
    },
    key_bluesky_influencers: {
      chart: "key_bluesky_influencers",
      component: "KeyPlatformInfluencers",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as TopInfluencersEvent;
        const { influencer } = eventData;

        const title = {
          keypath: "social_analyse_charts.mentions_by",
          url: `https://bsky.app/profile/${encodeURIComponent(influencer.source.username)}`,
          username: influencer.source.name,
        };

        return {
          filterProps: {
            authors: [influencer.source.name],
            platforms: [SocialPlatform.BLUESKY],
          },
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.key_bluesky_influencers.title"
      ),
      method: "key_bluesky_influencers",
      options: {
        size: 70,
      },
      style: { height: "384px" },
    },
    key_influencers: {
      chart: "key_influencers",
      component: "KeyPlatformInfluencers",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as TopInfluencersEvent;
        const { influencer } = eventData;

        return {
          filterProps: {
            authors: [influencer.source.username],
          },
          title: influencer.source.username,
        };
      },
      label: getLocaleValue("instant_insights_charts.key_influencers.title"),
      method: "key_influencers",
      options: {
        size: 70,
      },
      style: { height: "384px" },
    },
    key_instagram_influencers: {
      chart: "key_instagram_influencers",
      component: "KeyPlatformInfluencers",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as TopInfluencersEvent;
        const { influencer } = eventData;

        const title = {
          keypath: "social_analyse_charts.mentions_by",
          url: `https://instagram.com/${influencer.source.username}`,
          username: influencer.source.username,
        };

        return {
          filterProps: {
            authors: [influencer.source.username],
            platforms: [SocialPlatform.INSTAGRAM],
          },
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.key_instagram_influencers.title"
      ),
      method: "key_instagram_influencers",
      options: {
        size: 70,
      },
      style: { height: "384px" },
    },
    key_messages: {
      component: "KeyMessages",
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.key_messages.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.key_messages.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_horizontal_bar_chart.svg"),
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps) {
        const eventData = clickedProps as
          | DatasetClickEvent
          | DataPointClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const key = Object.keys(dataObj)[0];
        const data = dataObj[key];

        const { datasetIndex, label, value } = data;

        const { queries } = chartProps.options!;

        let keywords;
        let booleanQuery;

        const { streamId } = (chartProps.data as KeyMessagesData).datasets![
          datasetIndex
        ];

        const streamQueries = queries?.filter(
          (item) => item?.stream_id === streamId
        );

        const query = streamQueries?.length
          ? streamQueries.find((item) => item?.label === label)
          : queries?.find((item) => item?.label === label);

        if (query?.query_style === "keywords") {
          keywords = query.keywords;
        } else if (query?.query_style === "boolean") {
          booleanQuery = query.boolean;
        }

        const title = key;

        const selectedStream = findStreamById(streamId, streamsData);

        const isSocial = isSocialStream(selectedStream);

        const total = Number(value || 0);

        const subTitle = isSocial
          ? socialItemsString(total)
          : mediaItemsString(total);

        const isStackedVariant = [
          "bar_stacked",
          "bar_horizontal_stacked",
        ].includes(chartProps.options?.chart_variant || "");

        let media: Maybe<MediumName[]>;

        if (isSocial) {
          media = ["Social"];
        } else if (isStackedVariant && !isDatasetClickEvent(eventData)) {
          media = [key as MediumName];
        }

        return {
          filterProps: {
            ...(booleanQuery ? { booleanQuery } : {}),
            ...(keywords ? { keywords } : {}),
            ...(media ? { media } : {}),
          },
          selectedStream,
          subTitle,
          title,
        };
      },
      label: getLocaleValue("instant_insights_charts.key_messages.title"),
      method: "key_messages",
      options: {},
      settingsComponent: SidebarComponent.KeyMessagesForm,
      style: { ...BAR_DEFAULT, height: "345px" },
    },
    key_twitter_influencers: {
      chart: "key_twitter_influencers",
      component: "KeyPlatformInfluencers",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as TopInfluencersEvent;
        const { influencer } = eventData;

        const title = {
          keypath: "social_analyse_charts.mentions_by",
          url: `https://twitter.com/${influencer.source.username.replace(
            "@",
            ""
          )}`,
          username: influencer.source.username,
        };

        return {
          filterProps: {
            authors: [influencer.source.username],
            platforms: [SocialPlatform.TWITTER],
          },
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.key_twitter_influencers.title"
      ),
      method: "key_twitter_influencers",
      options: {
        size: 70,
      },
      style: { height: "384px" },
    },
    key_youtube_influencers: {
      chart: "key_youtube_influencers",
      component: "KeyPlatformInfluencers",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as TopInfluencersEvent;
        const { influencer } = eventData;

        const youtubeId = influencer.source.youtube_id;

        const title = {
          keypath: "social_analyse_charts.mentions_by",
          url: youtubeId
            ? `https://www.youtube.com/channel/${youtubeId}`
            : `https://www.youtube.com/results?search_query=${encodeURIComponent(influencer.source.name)}`,
          username: influencer.source.name,
        };

        return {
          filterProps: {
            authors: [influencer.source.name],
            platforms: [SocialPlatform.YOUTUBE],
          },
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.key_youtube_influencers.title"
      ),
      method: "key_youtube_influencers",
      options: {
        size: 70,
      },
      style: { height: "384px" },
    },
    leading_brands: {
      component: "WordCloud",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as WordCloudClickEvent;

        const { word } = eventData;

        const title = word;
        const subTitle = mediaItemsString(eventData.value!);

        return {
          filterProps: {
            keywords: word,
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue("instant_insights_charts.leading_brands.title"),
      method: "word_cloud",
      options: {
        entity_type: "organization",
        size: 30,
      },
      style: { ...DEFAULT_STYLE },
    },
    leading_hashtags: {
      chart: "word_cloud",
      component: "WordCloud",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as WordCloudClickEvent;

        const { word } = eventData;

        const title = word;
        const subTitle = socialItemsString(eventData.value!);

        return {
          filterProps: {
            keywords: word,
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue("instant_insights_charts.leading_hashtags.title"),
      method: "leading_hashtags",
      options: {
        size: 30,
      },
      style: {
        ...DEFAULT_STYLE,
        fontSize: "75%",
      },
    },
    leading_online_journalists: {
      component: "LeadingSources",
      featureFlag: "read_online_article",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as DataPointClickEvent;

        const journalist = Object.keys(eventData)[0];

        const title = journalist;

        const mentionsCount = Object.values(eventData[journalist])[0].value;

        const subTitle = mentionsCount
          ? mediaItemsString(Number(mentionsCount))
          : "";

        const authors = [journalist === "Unattributed" ? "" : journalist];

        return {
          filterProps: {
            authors,
            media: ["Online"],
            useExactAuthors: true,
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.leading_online_journalists.title"
      ),
      method: "leading_sources",
      options: {
        size: 10,
        visualisation_type: "online_journalists",
      },
      style: { ...BAR_DEFAULT },
    },
    leading_online_sources: {
      component: "LeadingSources",
      featureFlag: "read_online_article",
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartData) {
        const eventData = clickedProps as
          | DataPointClickEvent
          | DatasetClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const sourceName = Object.keys(eventData)[0];
        const { dataIndex, value } = Object.values(dataObj)[0];

        const { sourceKeys } = (chartData.data as LeadingSourcesData)
          .datasets[0];

        const [sourceMedium, sourceId] = sourceKeys![dataIndex]!.split("_");

        const title = sourceName;
        const subTitle = mediaItemsString(Number(value));

        return {
          filterProps: {
            media: ["Online"],
            sources: [
              {
                id: Number(sourceId),
                location: "",
                medium: sourceMedium as MediumField,
                name: "",
                type: "",
              },
            ],
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.leading_online_sources.title"
      ),
      method: "leading_sources",
      options: {
        size: 10,
        visualisation_type: "online_sources",
      },
      style: { ...BAR_DEFAULT },
    },
    leading_print_journalists: {
      component: "LeadingSources",
      featureFlag: "view_paper_articles",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as DataPointClickEvent;

        const journalist = Object.keys(eventData)[0];

        const title = journalist;

        const mentionsCount = Object.values(eventData[journalist])[0].value;

        const subTitle = mentionsCount
          ? mediaItemsString(Number(mentionsCount))
          : "";

        const authors = [journalist === "Unattributed" ? "" : journalist];

        return {
          filterProps: {
            authors,
            media: ["Print"],
            useExactAuthors: true,
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.leading_print_journalists.title"
      ),
      method: "leading_sources",
      options: {
        visualisation_type: "print_journalists",
      },
      style: { ...BAR_DEFAULT },
    },
    leading_print_sources: {
      component: "LeadingSources",
      featureFlag: "view_paper_articles",
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartData) {
        const eventData = clickedProps as DataPointClickEvent;

        const sourceName = Object.keys(eventData)[0];
        const { dataIndex, value } = Object.values(eventData[sourceName])[0];

        const { sourceKeys } = (chartData.data as LeadingSourcesData)
          .datasets[0];

        const [sourceMedium, sourceId] = sourceKeys![dataIndex]!.split("_");

        const title = sourceName;
        const subTitle = mediaItemsString(Number(value));

        return {
          filterProps: {
            media: ["Print"],
            sources: [
              {
                id: Number(sourceId),
                location: "",
                medium: sourceMedium as MediumField,
                name: "",
                type: "",
              },
            ],
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.leading_print_sources.title"
      ),
      method: "leading_sources",
      options: {
        size: 10,
        visualisation_type: "print_sources",
      },
      style: { ...BAR_DEFAULT },
    },
    leading_radio_programs: {
      component: "LeadingSources",
      featureFlag: "radio_mentions",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as DataPointClickEvent;
        const program = Object.keys(eventData)[0];

        const title = program;

        const mentionsCount = Object.values(eventData[program])[0].value;

        const subTitle = mentionsCount
          ? mediaItemsString(Number(mentionsCount))
          : "";

        return {
          filterProps: {
            media: ["Radio"],
            programs: [program],
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.leading_radio_programs.title"
      ),
      method: "leading_sources",
      options: {
        size: 10,
        visualisation_type: "radio_programs",
      },
      style: { ...BAR_DEFAULT },
    },
    leading_tv_programs: {
      component: "LeadingSources",
      featureFlag: "tv_mentions",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as DataPointClickEvent;
        const program = Object.keys(eventData)[0];

        const title = program;

        const mentionsCount = Object.values(eventData[program])[0].value;

        const subTitle = mentionsCount
          ? mediaItemsString(Number(mentionsCount))
          : "";

        return {
          filterProps: {
            media: ["TV"],
            programs: [program],
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.leading_tv_programs.title"
      ),
      method: "leading_sources",
      options: {
        size: 10,
        visualisation_type: "tv_programs",
      },
      style: { ...BAR_DEFAULT },
    },
    media_impact_timeline: {
      component: "BroadcastTimeByMediaType",
      interactionEnabled: false,
      label: getLocaleValue(
        "instant_insights_charts.media_impact_timeline.title"
      ),
      method: "broadcast_time_by_media_type",
      options: {},
      style: { ...DEFAULT_STYLE },
    },
    mention_benchmark_timeline: {
      component: "MentionBenchmarkTimeline",
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as MentionBenchmarkTimelineClickEvent;

        const dateKey = Object.keys(eventData)[0];

        const obj = eventData[dateKey];

        const innerValue = Object.values(obj)[0];

        const { value } = innerValue;

        const { date, y } = value;

        const { after, before } = clickedRange(date, chartProps, chartRange!);

        const title = formatIntlDate(date, {
          day: "numeric",
          month: "long",
          year: "numeric",
        });

        const subTitle = mediaItemsString(y);

        return {
          filterProps: {
            dateRangeEnabled: true,
            range: new DateRange({ after, before }),
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.mention_benchmark_timeline.title"
      ),
      legendStyle: {
        marginTop: "8px",
        width: "100%",
      },
      method: "mention_benchmark_timeline",
      options: {
        clip: {
          bottom: 10,
          left: 10,
          right: 10,
          top: 10,
        },
      },
      style: { ...DEFAULT_STYLE, height: "355px" },
    },
    mention_stream_insights_summary: {
      component: "MentionStreamInsightsSummary",
      label: getLocaleValue(
        "instant_insights_charts.mention_stream_insights_summary.title"
      ),
      method: "mention_stream_insights_summary",
    },
    mentions_benchmark: {
      component: "DataTableWithChangeAcrossPeriods",
      componentAttributes: {
        columnStyle: {
          color: "#2A3247",
          fontSize: "14px",
          fontWeight: "400",
          lineHeight: "16px",
          padding: "8px 20px",
        },
        headingStyle: {
          borderBottom: "1px solid #E5EAEF",
          color: "#586074",
          fontWeight: "400",
        },
        rowBorderDisabled: true,
        rowHoverStyle: {
          fontWeight: "700",
        },
      },
      label: "Mentions Benchmark",
      method: "mentions_benchmark",
      style: { ...DEFAULT_STYLE },
    },
    net_follower_growth: {
      allowsMultipleSocialAccounts: false,
      component: "CoverageVolumeTimeline",
      componentAttributes: {
        legendLayout: "inline",
      },
      label: getLocaleValue(
        "instant_insights_charts.net_follower_growth.title"
      ),
      method: "net_follower_growth",
      style: { ...TIMELINE_DEFAULT },
    },
    outreach_activity_timeline: {
      component: "CoverageVolumeTimeline",
      componentAttributes: {
        legendLayout: "inline",
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "outreach_activity_timeline.create_activity_link"
          ),
        },
        description: getLocaleValue(
          "outreach_activity_timeline.empty_table_message"
        ),
        image: assetsUrl("/images/charts/empty_line_chart.svg"),
      },
      interactionEnabled: false,
      label: getLocaleValue(
        "instant_insights_charts.outreach_activity_timeline.title"
      ),
      method: "outreach_activity_timeline",
      options: {},
      requiredPermissions: ["can_view_activities"],
      style: { ...TIMELINE_DEFAULT },
    },
    outreach_coverage_timeline: {
      component: "OutreachCoverageTimeline",
      componentAttributes: {
        legendLayout: "inline",
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.outreach_coverage_timeline.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.outreach_coverage_timeline.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_line_chart.svg"),
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as DataPointClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const dateKey = Object.keys(eventData)[0];

        const breakdownKey = Object.keys(dataObj)[0] as Nullable<MentionType>;

        const { datasetIndex, value } = dataObj[breakdownKey!];

        const { after, before } = clickedRange(
          dateKey,
          chartProps,
          chartRange!
        );

        const title = formatChartInteractionDate(dateKey, chartProps);

        const { streamId } = (chartProps.data as ChartLibraryData).datasets![
          datasetIndex
        ];

        const selectedStream = findStreamById(streamId, streamsData);

        const isSocial = isSocialStream(selectedStream);

        const total = Number(value || 0);

        const subTitle = isSocial
          ? socialItemsString(total)
          : mediaItemsString(total);

        return {
          filterProps: {
            dateRangeEnabled: true,
            range: new DateRange({ after, before }),
            ...(isSocial ? { media: ["Social"] } : {}),
          },
          selectedStream,
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.outreach_coverage_timeline.title"
      ),
      method: "outreach_coverage_timeline",
      requiredPermissions: ["can_view_media_release_and_analytics"],
      style: { ...TIMELINE_DEFAULT },
    },
    outreach_insights_summary: {
      component: "OutreachInsightsSummary",
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.outreach_performance_table.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.outreach_performance_table.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_table.svg"),
        style: {
          width: "auto",
        },
      },
      label: getLocaleValue(
        "instant_insights_charts.outreach_insights_summary.title"
      ),
      method: "outreach_insights_summary",
    },
    outreach_performance_table: {
      component: "OutreachPerformance",
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.outreach_performance_table.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.outreach_performance_table.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_table.svg"),
        style: {
          width: "auto",
        },
      },
      interactionEnabled: true,
      label: getLocaleValue(
        "instant_insights_charts.outreach_performance_table.title"
      ),
      method: "outreach_performance_table",
      options: {
        size: 30,
        visualisation_type: "table",
      },
      style: {
        padding: 0,
      },
    },
    outreach_top_engaged_contacts_table: {
      component: "OutreachTopEngagedContactsTable",
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.outreach_top_engaged_contacts_table.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.outreach_top_engaged_contacts_table.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_table.svg"),
        style: {
          width: "auto",
        },
      },
      interactionEnabled: true,
      label: getLocaleValue(
        "instant_insights_charts.outreach_top_engaged_contacts_table.title"
      ),
      method: "outreach_top_engaged_contacts_table",
      options: {
        size: 30,
        visualisation_type: "table",
      },
      style: {
        ...DEFAULT_STYLE,
        padding: 0,
      },
    },
    potential_audience_reach: {
      component: "PotentialAudienceReach",
      featureFlag: "dashboard_potential_reach",
      interactionEnabled: false,
      label: getLocaleValue(
        "instant_insights_charts.potential_audience_reach.title"
      ),
      method: "new_potential_audience_reach",
      options: {},
      style: { ...BAR_DEFAULT, height: "345px" },
    },
    react_score_breakdown: {
      component: "ReactScoreBreakdown",
      componentAttributes: {
        legendPosition: "right",
        truncateLabels: true,
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.react_score_breakdown.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.react_score_breakdown.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_pie_chart.svg"),
      },
      featureFlag: "has_react_score",
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as
          | DatasetClickEvent
          | DataPointClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const breakdownKey = Object.keys(dataObj)[0];
        const { dataIndex, value } = dataObj[breakdownKey];

        const title = (chartProps.data! as ChartLibraryData).labels![
          dataIndex
        ] as string;

        const subTitle = mediaItemsString(Number(value));

        const { range } = (chartProps.data as ReactScoreBreakdownData)
          .datasets[0];

        const { max, min } = range[dataIndex];

        return {
          filterProps: {
            range: chartRange,
            reactScore: { max: max * 100, min: min * 100 },
            reactScoreBreakdown: true,
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.react_score_breakdown.title"
      ),
      method: "react_score_breakdown",
      options: {},
      style: { ...TIMELINE_DEFAULT, overflow: "visible" },
    },
    react_score_timeline: {
      component: "SentimentTimeline",
      componentAttributes: {
        legendLayout: "inline",
      },
      featureFlag: "has_react_score",
      interactionEnabled: false,
      label: getLocaleValue(
        "instant_insights_charts.react_score_timeline.title"
      ),
      method: "react_score_timeline",
      options: {},
      style: { ...TIMELINE_DEFAULT },
    },
    sentiment_benchmark: {
      component: "SentimentBenchmark",
      componentAttributes: {
        legendLayout: "inline",
        legendPosition: "bottom",
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartData) {
        const eventData = clickedProps as
          | DataPointClickEvent
          | DatasetClickEvent;

        const dataObj = isDatasetClickEvent(eventData)
          ? eventData
          : Object.values(eventData)[0];

        const breakdownKey = Object.keys(dataObj)[0] as MediumName;

        const label = Object.keys(eventData)[0];
        const { dataIndex, datasetIndex } = dataObj[breakdownKey];

        const value = (chartData.data as ChartLibraryData).datasets![
          datasetIndex
        ].totals[dataIndex];

        const streamId = (chartData.data as ChartLibraryData).datasets![
          datasetIndex
        ].stream_ids[dataIndex];

        const selectedStream = findStreamById(streamId, streamsData);

        const isSocial = isSocialStream(selectedStream);

        const title = streamDisplayLabel(selectedStream, label);

        const subTitle = isSocial
          ? socialItemsString(Number(value))
          : mediaItemsString(Number(value));

        const sentimentOption = sentimentOptions.find(
          (option) => option.label === breakdownKey
        );

        const [min, max] = sentimentOption!.range;

        return {
          filterProps: {
            ...(isSocial ? { media: ["Social"] } : {}),
            sentiment: [{ max, min }],
            sentimentEnabled: true,
          },
          selectedStream,
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.sentiment_benchmark.title"
      ),
      method: "sentiment_benchmark",
      options: {},
      style: { ...BAR_DEFAULT },
    },
    sentiment_breakdown: {
      component: "SentimentBreakdown",
      componentAttributes: {
        legendPosition: "right",
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, _, chartRange) {
        const eventData = clickedProps as DatasetClickEvent;

        const label = Object.keys(eventData)[0];
        const { value } = eventData[label];

        const title = label;
        const subTitle = mediaItemsString(Number(value));

        const sentimentOption = sentimentOptions.find(
          (option) => option.label === label
        );

        const [min, max] = sentimentOption!.range;

        return {
          filterProps: {
            range: chartRange,
            sentiment: [{ max, min }],
            sentimentEnabled: true,
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.sentiment_breakdown.title"
      ),
      method: "sentiment_breakdown",
      options: {},
      style: { ...BAR_DEFAULT },
    },
    sentiment_timeline: {
      component: "SentimentTimeline",
      componentAttributes: {
        legendLayout: "inline",
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as DataPointClickEvent;

        const dateKey = Object.keys(eventData)[0];
        const sentimentLabel = Object.keys(eventData[dateKey])[0];

        const { datasetIndex, value } = eventData[dateKey][sentimentLabel];

        const { after, before } = clickedRange(
          dateKey,
          chartProps,
          chartRange!
        );

        const { field } = (chartProps.data as ChartLibraryData).datasets![
          datasetIndex
        ];

        const title = sentimentLabel;
        const subTitle = mediaItemsString(Number(value));

        const sentimentRange = sentimentOptions.find(
          (option) => option.field === field
        );

        const [min, max] = sentimentRange!.range;

        return {
          filterProps: {
            dateRangeEnabled: true,
            range: new DateRange({ after, before }),
            sentiment: [{ max, min }],
            sentimentEnabled: true,
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue("instant_insights_charts.sentiment_timeline.title"),
      method: "sentiment_timeline",
      options: {},
      style: { ...TIMELINE_DEFAULT },
    },
    share_of_voice: {
      component: "ShareOfVoice",
      componentAttributes: {
        legendPosition: "right",
        truncateLabels: true,
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.share_of_voice.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.share_of_voice.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_pie_chart.svg"),
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps) {
        const eventData = clickedProps as DatasetClickEvent;
        const { dataIndex, label, value } = Object.values(eventData)[0];

        const streamId = (chartProps.data as ShareOfVoiceData).datasets[0]
          ?.stream_ids?.[dataIndex];

        const selectedStream = streamId
          ? findStreamById(streamId, streamsData)
          : null;

        const title = streamDisplayLabel(selectedStream, label);

        const subTitle = isSocialStream(selectedStream)
          ? socialItemsString(Number(value))
          : mediaItemsString(Number(value));

        return {
          selectedStream,
          subTitle,
          title,
        };
      },
      label: getLocaleValue("instant_insights_charts.share_of_voice.title"),
      maxStreams: 30,
      method: "new_share_of_voice",
      options: {
        visualisation_type: "chart",
      },
      style: {
        ...PIE_DEFAULT,
        height: "320px",
        overflow: "visible",
      },
    },
    share_of_voice_follower_count: {
      component: "ShareOfVoice",
      componentAttributes: {
        legendPosition: "right",
        truncateLabels: true,
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.share_of_voice.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.share_of_voice.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_pie_chart.svg"),
      },
      label: getLocaleValue(
        "instant_insights_charts.share_of_voice_follower_count.title"
      ),
      maxStreams: 30,
      method: "share_of_voice_follower_count",
      options: {
        visualisation_type: "chart",
      },
      style: {
        ...PIE_DEFAULT,
        height: "320px",
        overflow: "visible",
      },
    },
    share_of_voice_social_post_engagement: {
      component: "ShareOfVoice",
      componentAttributes: {
        legendPosition: "right",
        truncateLabels: true,
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.share_of_voice_social_post_engagement.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.share_of_voice_social_post_engagement.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_pie_chart.svg"),
      },
      label: getLocaleValue(
        "instant_insights_charts.share_of_voice_social_post_engagement.title"
      ),
      maxSelectedPosts: 30,
      maxStreams: 30,
      method: "share_of_voice_social_post_engagement",
      options: {
        visualisation_type: "chart",
      },
      requiresSocialPostSelection: true,
      selectsFirstPostByDefault: true,
      style: {
        ...PIE_DEFAULT,
        height: "320px",
        overflow: "visible",
      },
    },
    share_of_voice_timeline: {
      component: "ShareOfVoiceTimeline",
      componentAttributes: {
        legendLayout: "inline",
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.share_of_voice_timeline.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.share_of_voice_timeline.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_line_chart.svg"),
      },
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as DataPointClickEvent;

        const label = Object.keys(Object.values(eventData)[0])[0];
        const { datasetIndex, value } = Object.values(eventData)[0][label];

        const { stream_id: streamId } = (
          chartProps.data as ShareOfVoiceTimelineData
        ).datasets[datasetIndex];

        const selectedStream = streamId
          ? findStreamById(streamId, streamsData)
          : null;

        let title = streamDisplayLabel(selectedStream, label);

        const subTitle = isSocialStream(selectedStream)
          ? socialItemsString(Number(value))
          : mediaItemsString(Number(value));

        const dateKey = Object.keys(clickedProps)[0];

        const { after, before } = clickedRange(
          dateKey,
          chartProps,
          chartRange!
        );

        title += ` (${formatChartInteractionDate(dateKey, chartProps)})`;

        return {
          filterProps: {
            dateRangeEnabled: true,
            range: new DateRange({ after, before }),
          },
          selectedStream,
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.share_of_voice_timeline.title"
      ),
      maxStreams: 30,
      method: "share_of_voice_timeline",
      options: {
        include_count_in_legend: true,
      },
      style: { ...TIMELINE_DEFAULT },
    },
    social_engage_performance: {
      component: "SocialEngagePerformance",
      label: getLocaleValue(
        "instant_insights_charts.social_engage_performance.title"
      ),
      method: "social_engage_performance",
    },
    social_insights_summary: {
      component: "SocialInsightsSummary",
      label: getLocaleValue(
        "instant_insights_charts.social_insights_summary.title"
      ),
      method: "social_insights_summary",
    },
    social_post_highlight: {
      component: "SocialPostHighlight",
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.social_post_highlight.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.social_post_highlight.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_table.svg"),
        style: {
          width: "auto",
        },
      },
      label: getLocaleValue(
        "instant_insights_charts.social_post_highlight.title"
      ),
      method: "social_post_highlight",
      requiresSocialPostSelection: true,
    },
    stream_vs_stream: {
      component: "ShareOfVoiceTimeline",
      componentAttributes: {
        legendLayout: "inline",
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.stream_vs_stream.empty_state.select_linked_stream"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.stream_vs_stream.empty_state.no_linked_stream"
        ),
        image: assetsUrl("/images/charts/empty_line_chart.svg"),
      },
      featureFlag: "has_social_platform",
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as DataPointClickEvent;

        const label = Object.keys(Object.values(eventData)[0])[0];
        const { datasetIndex, value } = Object.values(eventData)[0][label];

        const { stream_id: streamId } = (
          chartProps.data as ShareOfVoiceTimelineData
        ).datasets[datasetIndex];

        const selectedStream = streamId
          ? findStreamById(streamId, streamsData)
          : null;

        const isSocial = isSocialStream(selectedStream);

        let title = streamDisplayLabel(selectedStream, label);

        const subTitle = isSocial
          ? socialItemsString(Number(value))
          : mediaItemsString(Number(value));

        const dateKey = Object.keys(eventData)[0];

        const { after, before } = clickedRange(
          dateKey,
          chartProps,
          chartRange!
        );

        title += ` (${formatChartInteractionDate(dateKey, chartProps)})`;

        return {
          filterProps: {
            dateRangeEnabled: true,
            media: selectedStream?.enabled_media ?? [],
            range: new DateRange({ after, before }),
          },
          selectedStream,
          subTitle,
          title,
        };
      },
      label: getLocaleValue("instant_insights_charts.stream_vs_stream.title"),
      method: "stream_vs_stream",
      options: {
        include_count_in_legend: true,
      },
      requiredPermissions: ["can_use_listen", "can_use_analyse"],
      settingsComponent: SidebarComponent.LinkedStreamForm,
      style: { ...TIMELINE_DEFAULT },
    },
    themes: {
      component: "WordCloud",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as WordCloudClickEvent;

        const { streamId, value, word } = eventData;

        const selectedStream = streamId
          ? findStreamById(streamId, streamsData)
          : null;

        const isSocial = isSocialStream(selectedStream);

        const title = word;

        const subTitle = isSocial
          ? socialItemsString(Number(value))
          : mediaItemsString(Number(value));

        return {
          filterProps: {
            keywords: word,
            ...(isSocial ? { media: ["Social"] } : {}),
          },
          selectedStream,
          subTitle,
          title,
        };
      },
      label: getLocaleValue("instant_insights_charts.themes.title"),
      method: "word_cloud",
      options: {
        size: 30,
      },
      style: { ...WORD_CLOUD_DEFAULT },
    },
    top_articles_table: {
      component: "TopArticlesTable",
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.top_articles_table.empty_state.button_label"
          ),
        },
        description: getLocaleValue(
          "instant_insights_charts.top_articles_table.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_table.svg"),
        style: {
          width: "auto",
        },
      },
      featureFlag:
        "advertising_value_mentions,has_organisation_report_audience",
      interactionEnabled: true,
      label: getLocaleValue("instant_insights_charts.top_articles_table.title"),
      method: "top_articles_table",
      options: {
        size: 30,
        visualisation_type: "table",
      },
      style: {
        padding: 0,
      },
    },
    top_bluesky_posts: {
      chart: "top_bluesky_posts",
      component: "TopPlatformPosts",
      label: getLocaleValue("instant_insights_charts.top_bluesky_posts.title"),
      method: "top_bluesky_posts",
      options: {
        size: 70,
      },
      style: { height: "384px" },
    },
    top_facebook_pages: {
      chart: "top_facebook_pages",
      component: "TopFacebookPages",
      interactionEnabled: true,
      interactionFilterProps(clickedProps) {
        const eventData = clickedProps as TopFacebookPagesEvent;

        const { page } = eventData;

        const title = {
          keypath: "social_analyse_charts.mentions_by",
          url: page.source.url,
          username: page.source.username,
        };

        return {
          filterProps: {
            authors: [page.source.name],
            platforms: [SocialPlatform.FACEBOOK],
          },
          title,
        };
      },
      label: getLocaleValue("instant_insights_charts.top_facebook_pages.title"),
      method: "top_facebook_pages",
      options: {
        size: 70,
      },
      style: { height: "380px" },
    },
    top_facebook_posts: {
      chart: "top_facebook_posts",
      component: "TopPlatformPosts",
      label: getLocaleValue("instant_insights_charts.top_facebook_posts.title"),
      method: "top_facebook_posts",
      options: {
        size: 70,
      },
      style: { height: "440px" },
    },
    top_instagram_posts: {
      chart: "top_instagram_posts",
      component: "TopPlatformPosts",
      interactionEnabled: true,
      label: getLocaleValue(
        "instant_insights_charts.top_instagram_posts.title"
      ),
      method: "top_instagram_posts",
      options: {
        size: 70,
      },
      style: { height: "384px" },
    },
    top_linkedin_posts: {
      chart: "top_linkedin_posts",
      component: "TopPlatformPosts",
      label: getLocaleValue("instant_insights_charts.top_linkedin_posts.title"),
      method: "top_linkedin_posts",
      options: {
        size: 70,
      },
      style: { height: "384px" },
    },
    top_performing_post: {
      component: "SocialPostHighlight",
      label: getLocaleValue(
        "instant_insights_charts.top_performing_post.title"
      ),
      method: "top_performing_post",
    },
    top_posts: {
      chart: "top_posts",
      component: "TopPlatformPosts",
      label: getLocaleValue("instant_insights_charts.top_posts.title"),
      method: "top_posts",
      options: {
        size: 70,
      },
      style: { height: "440px" },
    },
    top_tiktok_videos: {
      chart: "top_tiktok_videos",
      component: "TopPlatformPosts",
      label: getLocaleValue("instant_insights_charts.top_tiktok_videos.title"),
      method: "top_tiktok_videos",
      options: {
        size: 70,
      },
      style: { height: "384px" },
    },
    top_tweets: {
      chart: "top_tweets",
      component: "TopPlatformPosts",
      label: getLocaleValue("instant_insights_charts.top_tweets.title"),
      method: "top_tweets",
      options: {
        size: 70,
      },
      style: { height: "440px" },
    },
    top_youtube_videos: {
      chart: "top_youtube_videos",
      component: "TopPlatformPosts",
      label: getLocaleValue("instant_insights_charts.top_youtube_videos.title"),
      method: "top_youtube_videos",
      options: {
        size: 70,
      },
      style: { height: "384px" },
    },
    total_audience_reach: {
      component: "PotentialAudienceReach",
      featureFlag: "dashboard_potential_reach",
      interactionEnabled: false,
      label: getLocaleValue(
        "instant_insights_charts.total_audience_reach.title"
      ),
      method: "total_audience_reach",
      options: {},
      style: { ...BAR_DEFAULT, height: "345px" },
    },
    total_social_shares: {
      component: "TotalSocialShares",
      interactionEnabled: true,
      interactionFilterProps(clickedProps, chartProps, chartRange) {
        const eventData = clickedProps as
          | DatasetClickEvent
          | DataPointClickEvent;

        let dataPoint: DataPoint;
        let dateKey: string = "";
        let platformLabel: string = "";
        let title: string;
        let rangeForFilter: DateRange = chartRange!;

        let platform: Nullable<ShareTypePlatform> = null;
        const chartData = chartProps.data as ChartLibraryData;

        if (isDataPointClickEvent(eventData)) {
          [dateKey] = Object.keys(eventData);
          [platformLabel] = Object.keys(eventData[dateKey]);
          dataPoint = eventData[dateKey][platformLabel];

          const clickedDates = clickedRange(dateKey, chartProps, chartRange!);
          rangeForFilter = new DateRange(clickedDates);

          title = formatChartInteractionDate(dateKey, chartProps);

          platform =
            chartData.datasets?.[dataPoint.datasetIndex].platform ?? null;
        } else {
          [platformLabel] = Object.keys(eventData);

          dataPoint = eventData[platformLabel];
          title = platformLabel;
          platform = chartData.platforms?.[dataPoint.dataIndex] ?? null;
        }

        const { value } = dataPoint;

        const shareTypes =
          platform === null
            ? Object.values(SHARE_TYPES_BY_PLATFORM).flat()
            : SHARE_TYPES_BY_PLATFORM[platform];

        const subTitle = getLocaleText(
          "instant_insights_charts.shares_by_type",
          {
            type: platformLabel,
            value: prettyNumber(value),
          }
        );

        return {
          filterProps: {
            dateRangeEnabled: true,
            media: ["Online"],
            range: rangeForFilter,
            shareTypes: [...shareTypes],
          },
          subTitle,
          title,
        };
      },
      label: getLocaleValue(
        "instant_insights_charts.total_social_shares.title"
      ),
      method: "total_social_shares",
      style: { ...TIMELINE_DEFAULT },
    },
    trajaan_sentiment: {
      component: "SentimentBreakdown",
      componentAttributes: {
        legendPosition: "right",
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.trajaan.empty_state.button_label"
          ),
        },
        buttonTooltip: aiVisibilityButtonTooltip,
        description: getLocaleValue(
          "instant_insights_charts.trajaan.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_line_chart.svg"),
        isButtonDisabled: isAIVisibilityButtonDisabled,
      },
      interactionEnabled: true,
      interactionFilterProps() {
        return {};
      },
      label: getLocaleValue("instant_insights_charts.trajaan_sentiment.title"),
      method: "trajaan_sentiment",
      options: {
        limit: 100,
      },
      style: { ...PIE_DEFAULT },
    },
    trajaan_top_authors: {
      component: "AIVisibilityDataTable",
      componentAttributes: {
        legendPosition: "right",
        truncateLabels: true,
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.trajaan.empty_state.button_label"
          ),
        },
        buttonTooltip: aiVisibilityButtonTooltip,
        description: getLocaleValue(
          "instant_insights_charts.trajaan.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_line_chart.svg"),
        isButtonDisabled: isAIVisibilityButtonDisabled,
      },
      interactionEnabled: true,
      interactionFilterProps() {
        return {};
      },
      label: getLocaleValue(
        "instant_insights_charts.trajaan_top_authors.title"
      ),
      method: "trajaan_top_authors",
      options: {
        limit: 50,
      },
      style: {
        ...DEFAULT_STYLE,
        padding: 0,
      },
    },
    trajaan_top_domains: {
      component: "AIVisibilityDataTable",
      componentAttributes: {
        legendPosition: "right",
        truncateLabels: true,
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.trajaan.empty_state.button_label"
          ),
        },
        buttonTooltip: aiVisibilityButtonTooltip,
        description: getLocaleValue(
          "instant_insights_charts.trajaan.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_line_chart.svg"),
        isButtonDisabled: isAIVisibilityButtonDisabled,
      },
      interactionEnabled: true,
      interactionFilterProps() {
        return {};
      },
      label: getLocaleValue(
        "instant_insights_charts.trajaan_top_domains.title"
      ),
      method: "trajaan_top_domains",
      options: {
        limit: 100,
      },
      style: {
        ...DEFAULT_STYLE,
        padding: 0,
      },
    },
    trajaan_top_outlets: {
      component: "AIVisibilityDataTable",
      componentAttributes: {
        legendPosition: "right",
        truncateLabels: true,
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.trajaan.empty_state.button_label"
          ),
        },
        buttonTooltip: aiVisibilityButtonTooltip,
        description: getLocaleValue(
          "instant_insights_charts.trajaan.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_line_chart.svg"),
        isButtonDisabled: isAIVisibilityButtonDisabled,
      },
      interactionEnabled: true,
      interactionFilterProps() {
        return {};
      },
      label: getLocaleValue(
        "instant_insights_charts.trajaan_top_outlets.title"
      ),
      method: "trajaan_top_outlets",
      options: {
        limit: 50,
      },
      style: {
        ...DEFAULT_STYLE,
        padding: 0,
      },
    },
    trajaan_visibility: {
      component: "AIVisibilityDataTable",
      componentAttributes: {
        legendPosition: "right",
        truncateLabels: true,
      },
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.trajaan.empty_state.button_label"
          ),
        },
        buttonTooltip: aiVisibilityButtonTooltip,
        description: getLocaleValue(
          "instant_insights_charts.trajaan.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_line_chart.svg"),
        isButtonDisabled: isAIVisibilityButtonDisabled,
      },
      featureFlag: "has_ai_visibility",
      interactionEnabled: true,
      interactionFilterProps() {
        return {};
      },
      label: getLocaleValue("instant_insights_charts.trajaan_visibility.title"),
      method: "trajaan_visibility",
      options: {
        limit: 100,
      },
      style: {
        ...DEFAULT_STYLE,
        padding: 0,
      },
    },
    trajaan_word_cloud: {
      component: "WordCloud",
      emptyState: {
        button: {
          label: getLocaleValue(
            "instant_insights_charts.trajaan.empty_state.button_label"
          ),
        },
        buttonTooltip: aiVisibilityButtonTooltip,
        description: getLocaleValue(
          "instant_insights_charts.trajaan.empty_state.description"
        ),
        image: assetsUrl("/images/charts/empty_line_chart.svg"),
        isButtonDisabled: isAIVisibilityButtonDisabled,
      },
      label: getLocaleValue("instant_insights_charts.trajaan_word_cloud.title"),
      method: "trajaan_word_cloud",
      options: {
        size: 30,
      },
      style: { ...WORD_CLOUD_DEFAULT },
    },
    user_rated_sentiment: {
      component: "SentimentBreakdown",
      componentAttributes: {
        legendPosition: "right",
      },
      interactionEnabled: false,
      label: getLocaleValue(
        "instant_insights_charts.user_rated_sentiment.title"
      ),
      method: "new_user_rated_sentiment",
      options: {},
      style: { ...PIE_DEFAULT },
    },
  };
}
